import React, { useState } from 'react';
import { Offcanvas } from 'react-bootstrap';
import { userStore } from 'store/UserStore';
import './referral.css';

interface PropsType {
  handleModal: any;
  isShow: boolean;
}

const WhatsAppIcon = ({ size = 52, ...props }: { size?: number } & React.SVGProps<SVGSVGElement>) => (
  <svg width={size} height={size} viewBox='0 0 48 48' {...props}>
    <circle cx='24' cy='24' r='24' fill='#25D366' />
    <g transform='translate(10,10) scale(1.1667)' fill='#fff'>
      <path d='M17.472 14.382c-.297-.149-1.758-.867-2.03-.966-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.075-.124-.272-.198-.57-.347z' />
      <path d='M12.041 21.785h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884zM20.52 3.449C18.24 1.242 15.24 0 12.045 0 5.463 0 .112 5.35.109 11.929c0 2.103.548 4.157 1.588 5.967L0 24l6.259-1.641a11.86 11.86 0 005.876 1.508h.005c6.582 0 11.933-5.35 11.936-11.929a11.86 11.86 0 00-3.556-8.489z' />
    </g>
  </svg>
);

const MailIcon = ({ size = 52, ...props }: { size?: number } & React.SVGProps<SVGSVGElement>) => (
  <svg width={size} height={size} viewBox='0 0 48 48' {...props}>
    <circle cx='24' cy='24' r='24' fill='#2880d7ff' />
    <path d='M12 16h24a2 2 0 012 2v12a2 2 0 01-2 2H12a2 2 0 01-2-2V18a2 2 0 012-2z' fill='#fff' />
    <path
      d='M11 17l13 9 13-9'
      fill='none'
      stroke='#2F6FE4'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

const ChevronDownIcon = ({ size = 16, ...props }: { size?: number } & React.SVGProps<SVGSVGElement>) => (
  <svg
    width={size}
    height={size}
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
    {...props}
  >
    <polyline points='6 9 12 15 18 9' />
  </svg>
);

const Referral: React.FC<PropsType> = ({ handleModal, isShow }) => {
  const customer = userStore((state: any) => state.customer);
  const [copied, setCopied] = useState(false);
  const [showTerms, setShowTerms] = useState(false);

  const code = customer?.referralCode;
  const rewardAmount = '50';
  const minInvestment = '1,000';
  const investmentWindowDays: number = 7;

  const shareMessage =
    customer?.referralMsg ??
    `Hey 👋, I've been investing in Digital Gold, Silver, Platinum, and Palladium with eBullion. It's simple to get started, with investments as low as ₹10.\n\nTo begin your investing experience, register using my referral code or link.\n\n🎁 We will each receive ₹${rewardAmount} as a reward as soon as you complete ₹${minInvestment} worth of investments within ${investmentWindowDays} days of signing up!\n\n🪙 Code: *${code}*\n🔗 https://secure.ebullion.in/?ref=${code}&method=rfr\n\nT&Cs apply.`;

  const STEPS = [
    {
      n: '01',
      title: 'Share your code',
      body: "Send your link to a friend who hasn't joined eBullion yet.",
    },
    {
      n: '02',
      title: 'They sign up & invest',
      body: `Your friend creates an account and invests ₹${minInvestment} or more within ${investmentWindowDays} days.`,
    },
    {
      n: '03',
      title: `You both get ₹${rewardAmount} each`,
      body: 'Rewards get credited to your individual eBullion wallet.',
    },
  ];

  const TERMS = [
    {
      title: '1. Eligibility',
      points: [
        'The referral program is open to registered eBullion users with an active account.',
        "Both the referrer and the referred person must comply with eBullion's Terms of Use and applicable laws.",
      ],
    },
    {
      title: '2. Referral Reward',
      points: [
        `The referrer will receive a reward of ₹${rewardAmount}.`,
        `The referred person will also receive a reward of ₹${rewardAmount}.`,
        'Rewards will be credited only after all eligibility conditions are successfully met.',
      ],
    },
    {
      title: '3. Qualifying Transaction',
     points: [
  "The referred person must sign up using the referrer's unique referral code or referral link.",
  `The referred person must successfully complete one or more eligible transactions totalling ₹${minInvestment} or more within ${investmentWindowDays} day${investmentWindowDays === 1 ? '' : 's'} on eBullion.`,
  'Only successful and completed transactions are considered. Failed, cancelled, reversed, refunded, or disputed transactions will not qualify.',
],
    },
    {
      title: '4. Reward Credit',
      points: [
        'Eligible rewards will be credited to both users within the timeline communicated by eBullion after successful verification.',
        'Reward credits may be subject to internal verification and fraud checks.',
      ],
    },
    {
      title: '5. Referral Validity',
      points: [
        'A referred person can use only one referral code at the time of registration.',
        'Once a referral code has been applied, it cannot be changed.',
      ],
    },
    {
      title: '6. Abuse & Fraud Prevention',
      intro: 'eBullion reserves the right to withhold, reverse, or cancel rewards if it detects:',
      points: [
        'Self-referrals.',
        'Multiple accounts created by the same individual.',
        'Fake, duplicate, or fraudulent accounts.',
        'Transactions intended solely to obtain referral rewards.',
        'Any suspicious, abusive, or fraudulent activity.',
        "Violation of eBullion's Terms of Use or applicable laws.",
      ],
    },
    {
      title: '7. Reward Usage',
      points: [
        'Referral rewards are non-transferable and cannot be exchanged for cash unless expressly permitted by eBullion.',
        'Rewards may be subject to specific usage conditions communicated by eBullion.',
      ],
    },
    {
      title: '8. Taxes',
      points: [
        'Any applicable taxes arising from the referral reward shall be the responsibility of the respective user, wherever required under applicable law.',
      ],
    },
    {
      title: '9. Program Modification',
      points: [
        'eBullion reserves the right to modify, suspend, extend, or terminate the referral program at any time without prior notice.',
        'eBullion may revise the reward amount, eligibility criteria, qualifying transaction value, or any other program terms at its sole discretion.',
      ],
    },
    {
      title: '10. Final Decision',
      points: [
        'All decisions made by eBullion regarding eligibility, verification, reward credits, and disputes shall be final and binding.',
      ],
    },
    {
      title: '11. General',
      points: [
        'Participation in the referral program constitutes acceptance of these Terms & Conditions.',
        'The referral program is subject to the laws of India and the jurisdiction of the competent courts.',
      ],
    },
  ];

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
    } catch (err) {
      console.log(err);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  const handleShare = async () => {
    if ((navigator as any).share) {
      try {
        await (navigator as any).share({
          // title: 'Join me on eBullion',
          text: shareMessage,
        });
      } catch (err) {
        console.log(err);
      }
    } else {
      handleCopy();
    }
  };

  return (
    <Offcanvas
      show={isShow}
      onHide={() => handleModal(false)}
      placement="end"
      className="referral-offcanvas"
    >
      <Offcanvas.Header
        closeButton
        closeVariant="white"
        className="referral-offcanvas-header"
      >
        <div>
          <h1 className="referral-header-title">Refer a Friend</h1>
        </div>
      </Offcanvas.Header>

      <Offcanvas.Body className="referral-offcanvas-body">
        <div className="referral-scroll-area">
          <p className="referral-intro-text">
            Invite your friends to eBullion. When they sign up using your referral code and invest a total of ₹{minInvestment} within {investmentWindowDays} days, you both receive ₹{rewardAmount}.
          </p>

          <div className="referral-code-card">
            <div className="referral-code-info">
              <span className="referral-code-label">Your code</span>
              <span className="referral-code-value">{code}</span>
            </div>
            <button
              type="button"
              className={`referral-copy-btn${copied ? ' referral-copy-btn--copied' : ''}`}
              onClick={handleCopy}
            >
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>

          <button type="button" className="referral-share-btn" onClick={handleShare}>
            Copy and share your link
          </button>

          <div className="referral-share-row">
            <a
              href={`https://wa.me/?text=${encodeURIComponent(shareMessage)}`}
              target="_blank"
              rel="noreferrer"
              className="referral-share-link"
            >
              <WhatsAppIcon size={16} aria-hidden="true" />
              Share via WhatsApp
            </a>
            <a
              href={`mailto:?subject=${encodeURIComponent(
                'Join me on eBullion'
              )}&body=${encodeURIComponent(shareMessage)}`}
              className="referral-share-link"
            >
              <MailIcon size={16} aria-hidden="true" />
              Share via Email
            </a>
          </div>

          <div className="referral-steps-section">
            <span className="referral-count-label">How it works</span>
            {STEPS.map((step) => (
              <div key={step.n} className="referral-step-card">
                <span className="referral-step-number">{step.n}</span>
                <div className="referral-step-content">
                  <h3 className="referral-step-title">{step.title}</h3>
                  <p className="referral-step-body">{step.body}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="referral-terms-section">
            <button
              type="button"
              className="referral-terms-toggle"
              onClick={() => setShowTerms((prev) => !prev)}
              aria-expanded={showTerms}
              aria-controls="referral-terms-panel"
            >
              <span>Terms and Conditions Apply</span>
              <ChevronDownIcon
                size={16}
                className={`referral-terms-chevron${showTerms ? ' referral-terms-chevron--open' : ''}`}
                aria-hidden="true"
              />
            </button>

            {showTerms && (
              <div id="referral-terms-panel" className="referral-terms-content">
                <h4 className="referral-terms-title">
                  eBullion Refer &amp; Earn Program – Terms &amp; Conditions
                </h4>
                <p className="referral-terms-subtitle">
                  Refer your friends to eBullion and earn rewards together!
                </p>

                {TERMS.map((section) => (
                  <div key={section.title} className="referral-terms-block">
                    <h5 className="referral-terms-heading">{section.title}</h5>
                    {section.intro && (
                      <p className="referral-terms-block-intro">{section.intro}</p>
                    )}
                    <ul className="referral-terms-list">
                      {section.points.map((point) => (
                        <li key={point}>{point}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </Offcanvas.Body>
    </Offcanvas>
  );
};

export default Referral;